import * as swcHelpers from '@swc/helpers';
import * as React from 'react';
import { DataLayer } from '@grandvisionhq/analytics';
import { useIntl, useFeatureToggles } from '@grandvisionhq/state';
import { Util } from '@grandvisionhq/stores-v2';
import { useTrackPageView } from '@grandvisionhq/svc-tealium/events';
import { useScrollIntoView, useDebouncedEffect, DateUtils } from '@grandvisionhq/utils';
import { useTimeFilterContext } from '../../context/TimeFilterContext/TimeFilterContext.js';
import { appointmentBookingAlternativeLocationEvent, bookAppointmentWhenStepScreenEvent } from '../../data-layer/actions.js';
import { usePreferredTimeDataLayerEventDispatchers } from '../../data-layer/hooks/use-preferred-time-dl-event-dispatchers.js';
import '@grandvisionhq/graphql';
import '../../queries/get-availability-stores-nearby.js';
import useCalendarAvailabilityById from '../../hooks/api/use-calendar-availability-by-id.js';
import '../../queries/book-appointment.js';
import '../../queries/book-appointment-by-id.js';
import '../../queries/get-user.js';
import '../../queries/get-calendar-availability-by-id.js';
import '../../queries/get-store-offer-by-code.js';
import '../../queries/get-store-offer-by-id.js';
import '../../queries/get-store-opening-times.js';
import '../../queries/get-store-opening-times-by-id.js';
import '../../queries/get-time-slots-by-id.js';
import { useStoreOpeningTimesById } from '../../hooks/api/use-store-opening-times-by-id.js';
import useTimeSlotsById from '../../hooks/api/use-time-slots-by-id.js';
import { useActiveTimeFilters } from '../../hooks/use-active-time-filters.js';
import { useAppointmentDurationLabel } from '../../hooks/use-appointment-duration-label.js';
import { useAppointmentContext } from '../../pages/BookAppointment/context/AppointmentContext.js';
import { useRoutingContext } from '../../pages/BookAppointment/context/RoutingContext/context.js';
import useBookAppointmentStoreById from '../../pages/BookAppointment/hooks/api/use-book-appointment-store-by-id.js';
import useBookAppointmentType from '../../pages/BookAppointment/hooks/api/use-book-appointment-type.js';
import useBookAppointmentStoreWithUrlParam from '../../pages/BookAppointment/hooks/use-book-appointment-store-with-url-param.js';
import useBookAppointmentTypeWithUrlParam from '../../pages/BookAppointment/hooks/use-book-appointment-type-with-url-param.js';
import useVirtualAppointment from '../../pages/BookAppointment/hooks/use-virtual-appointment.js';
import { isPreferredTimeFilterActive } from '../../utils/is-preferred-time-filter-active.js';
import { getWeekDaysForWeeksFromDate } from '../../utils/datetime.js';
import { getClosedDates, firstAvailableDate, getCalendarTimeSlotsAvailability } from '../AvailabilityCalendar/utils.js';
import { useNextAvailableDate } from '../NoTimeSlots/hooks/use-next-available-date.js';
import { useNextWeekDate } from '../NoTimeSlots/hooks/use-next-week-date.js';
import { useNoTimeSlotsVariant } from '../NoTimeSlots/hooks/use-no-timeslot-variant.js';
import { Variant } from '../NoTimeSlots/types.js';
import AppointmentDateTime from './AppointmentDateTime.js';
import useShouldRequestAlternativeStoreSuggestions from './useShouldRequestAlternativeStoreSuggestions.js';

const AppointmentDateTimeContainer = (_param)=>{
    var { shouldRequestAvailabilityOnMount =true  } = _param, rest = swcHelpers.objectWithoutProperties(_param, [
        "shouldRequestAvailabilityOnMount"
    ]);
    const { getLabel , getFormattedLabel , locale  } = useIntl();
    const [{ setStore , setTimeSlot  }, { store: store1 , type , timeSlot: timeSlot1  }] = useAppointmentContext();
    const [{ goTo  }, { queryParams  }] = useRoutingContext();
    const [{ isVirtualAppointmentType  }] = useVirtualAppointment();
    const isVirtualAppointment = isVirtualAppointmentType(type);
    const { elementRef: otherTimeSlotsElement , triggerScroll  } = useScrollIntoView({
        block: 'center'
    });
    const [, { active  }] = useTimeFilterContext();
    const [{ getAvailabilityTimeFilters  }, { activeFilters: activeTimeFilters  }] = useActiveTimeFilters();
    const today = React.useRef(new Date());
    const [weekDate, setWeekDate] = React.useState(()=>{
        var // first day of first week
        ref, ref1;
        return (ref = getWeekDaysForWeeksFromDate(today.current)) === null || ref === void 0 ? void 0 : (ref1 = ref[0]) === null || ref1 === void 0 ? void 0 : ref1[0];
    });
    const [weeks1, setWeeks] = React.useState();
    const [date1, setDate] = React.useState();
    const [availabilityRequestParamsChanged, setAvailabilityRequestParamsChanged] = React.useState(shouldRequestAvailabilityOnMount);
    const [showOtherTimeSlots, setShowOtherTimeSlots] = React.useState(false);
    const { isMultilingualEnabled , featureConfigs , isAabAlternativeStoreAvailabilityEnabled  } = useFeatureToggles();
    const { appointmentBookingUrlPaddedLength , appointmentBookingAlternativeUrl , appointmentBookingAlternativeUrlStores ,  } = featureConfigs;
    const [getCalendarAvailability, calendarAvailability] = useCalendarAvailabilityById();
    const [, bookAppointmentType] = useBookAppointmentType();
    const [, bookAppointmentStore] = useBookAppointmentStoreById();
    const [getStoreOpeningTimes, storeOpeningTimes] = useStoreOpeningTimesById();
    /** Refetch store and appointment type data if has different typeIs, storeId in the url
   * and store code with appointment type id in the context.
   * This can be possible when user use back and forth browser button to navigate between steps */ useBookAppointmentStoreWithUrlParam();
    useBookAppointmentTypeWithUrlParam();
    const appointmentBookingAvailabiltyCalendarWeekCount = featureConfigs === null || featureConfigs === void 0 ? void 0 : featureConfigs.appointmentBookingAvailabiltyCalendarWeekCount;
    const durationLabel = useAppointmentDurationLabel(type);
    const labels = React.useMemo(()=>{
        return {
            title: getLabel('appointments.component.appointmentDateTimeContainer.title'),
            selectATime: getLabel('appointments.component.appointmentDateTimeContainer.selectATime'),
            selectATimeSubtitle: durationLabel ? getFormattedLabel('appointments.component.appointmentDateTimeContainer.selectATimeSubtitle', {
                labelData: {
                    duration: durationLabel
                }
            }) : '',
            showOtherTimeSlots: getLabel('appointments.component.appointmentDateTimeContainer.showOtherTimeSlots'),
            hideOtherTimeSlots: getLabel('appointments.component.appointmentDateTimeContainer.hideOtherTimeSlots'),
            otherTimeSlots: getLabel('appointments.component.appointmentDateTimeContainer.otherTimeSlots')
        };
    }, [
        durationLabel,
        getLabel,
        getFormattedLabel
    ]);
    const closedDates = React.useMemo(()=>{
        if (!weeks1 || !storeOpeningTimes.data) {
            return [];
        }
        return getClosedDates(weeks1, storeOpeningTimes.data);
    }, [
        weeks1,
        storeOpeningTimes.data
    ]);
    const requestCalendarAvailability = React.useCallback(()=>{
        const appointmentTypeId = type === null || type === void 0 ? void 0 : type.id;
        const globalStoreId = store1 === null || store1 === void 0 ? void 0 : store1.globalStoreId;
        if (!appointmentTypeId || !globalStoreId || !weeks1) {
            /* istanbul ignore else */ if (process.env.NODE_ENV === 'test') {
                console.log('Awaiting availability request variables data') // TODO: This should not be here.
                ;
            }
            return;
        }
        const firstWeek = weeks1[0];
        const firstWeekDay = firstWeek === null || firstWeek === void 0 ? void 0 : firstWeek[0];
        const lastWeek = weeks1[weeks1.length - 1];
        const lastWeekDay = lastWeek === null || lastWeek === void 0 ? void 0 : lastWeek[lastWeek.length - 1];
        if (firstWeekDay && lastWeekDay) {
            getCalendarAvailability(swcHelpers.objectSpread({
                appointmentTypeId,
                globalStoreId,
                startDate: firstWeekDay,
                endDate: lastWeekDay
            }, getAvailabilityTimeFilters()));
        } else {
            console.error('Missing calendar data: startDate or endDate is undefined');
        }
        setAvailabilityRequestParamsChanged(false);
    }, [
        getAvailabilityTimeFilters,
        getCalendarAvailability,
        store1 === null || store1 === void 0 ? void 0 : store1.globalStoreId,
        type === null || type === void 0 ? void 0 : type.id,
        weeks1
    ]);
    /** Triggering 14 re-renders --> getCalendarAvailability && setAvailabilityRequestParamsChanged */ useDebouncedEffect(()=>{
        if (!calendarAvailability) {
            requestCalendarAvailability();
        }
    }, 400, [
        requestCalendarAvailability
    ]);
    React.useEffect(()=>{
        if (calendarAvailability) {
            requestCalendarAvailability();
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [
        requestCalendarAvailability
    ]);
    const onCalendarWeeksChange = React.useCallback((weeks)=>{
        setAvailabilityRequestParamsChanged(true);
        setWeeks(weeks);
    }, []);
    const onSelectDate = React.useCallback((date)=>{
        setDate(date);
    }, []);
    const [getTimeSlots, timeSlots] = useTimeSlotsById({
        timeFilters: activeTimeFilters
    });
    const { onPreferredTimeClick , onPreferredTimeFilterClick  } = usePreferredTimeDataLayerEventDispatchers({
        appointmentCondition: 'new',
        isVirtualAppointment,
        type,
        store: store1,
        timeSlot: timeSlot1
    });
    const calendarLoading = timeSlots.loading || bookAppointmentStore.loading || bookAppointmentType.loading || calendarAvailability.loading || storeOpeningTimes.loading || availabilityRequestParamsChanged;
    const nextAvailableDate = useNextAvailableDate(date1, calendarAvailability, Boolean(!timeSlots.called || calendarLoading));
    const nextWeekDate = useNextWeekDate(weeks1, !date1 || Boolean(nextAvailableDate));
    const noTimeSlotsVariant = useNoTimeSlotsVariant(timeSlots, nextAvailableDate, date1, closedDates, calendarLoading, isPreferredTimeFilterActive(active));
    const onSelectTimeSlot = React.useCallback((timeSlot)=>{
        setTimeSlot(timeSlot);
        const { typeId , storeId , search , userLocation  } = queryParams;
        goTo('who', {
            typeId,
            storeId,
            start: `${timeSlot.from.getTime()}`,
            end: `${timeSlot.until.getTime()}`,
            search,
            userLocation
        });
    }, [
        setTimeSlot,
        goTo,
        queryParams
    ]);
    const pushClickedOnAlternativeStoreEvent = React.useCallback((store)=>{
        if (type && store.globalStoreId) {
            const { id: typeId , ageGroup  } = type;
            const dataLayerEvent = appointmentBookingAlternativeLocationEvent({
                condition_of_appointment: 'new',
                ageGroup,
                appointment: {
                    type_of_appointment: isVirtualAppointment ? 'videocall' : 'onsite',
                    type_of_service: typeId
                },
                storeDetails: swcHelpers.objectSpread({
                    id: store.globalStoreId,
                    city: store.town
                }, store),
                text_of_the_element: store.name,
                // timeslots are always going to be undefined in this case
                time: {}
            });
            DataLayer.pushEvent(dataLayerEvent);
        }
    }, [
        type,
        isVirtualAppointment
    ]);
    const onSelectStore = React.useCallback((store, newDate)=>{
        pushClickedOnAlternativeStoreEvent(store);
        const invalidUrl = '##invalid_url##' // If no alternative url can be generated, this will be used in the result
        ;
        var _globalStoreId;
        const alternativeLocation = Util.getBookAppointmentUrl(invalidUrl, {
            paddedLength: appointmentBookingUrlPaddedLength,
            alternativeUrl: appointmentBookingAlternativeUrl && isMultilingualEnabled ? appointmentBookingAlternativeUrl[locale] : appointmentBookingAlternativeUrl,
            alternativeUrlStores: appointmentBookingAlternativeUrlStores
        })(swcHelpers.objectSpread({}, store, {
            globalStoreId: (_globalStoreId = store.globalStoreId) !== null && _globalStoreId !== void 0 ? _globalStoreId : store.code.toString()
        }));
        if (!alternativeLocation.includes(invalidUrl)) {
            // A valid alternative location was generated
            window.location.assign(alternativeLocation);
            return;
        }
        setStore(store);
        /* sometimes the date is the same but the time is different, so we
        check to avoid unnecessary rerenders */ const isNotTheSameDay = (d1, d2)=>{
            const date1 = d1 ? DateUtils.toLocalDate(d1).split('T')[0] : undefined;
            const date2 = d2 ? DateUtils.toLocalDate(d2).split('T')[0] : undefined;
            return date1 !== date2;
        };
        if (newDate) {
            var ref, ref2, ref3;
            const newWeeks = getWeekDaysForWeeksFromDate(newDate);
            const newWeekDate = (ref = newWeeks[0]) === null || ref === void 0 ? void 0 : ref[0];
            /* setting weekDate is required for our calendar to
          reload if the new date is not within our current weeks */ // dont reload calendar if not needed
            if (isNotTheSameDay(newWeekDate, weekDate)) {
                setWeekDate(newWeekDate);
            }
            // dont reload calendar if not needed
            if (isNotTheSameDay((ref2 = newWeeks[0]) === null || ref2 === void 0 ? void 0 : ref2[0], weeks1 === null || weeks1 === void 0 ? void 0 : (ref3 = weeks1[0]) === null || ref3 === void 0 ? void 0 : ref3[0])) {
                setWeeks(newWeeks);
            }
            // dont reload calendar if not needed
            if (isNotTheSameDay(date1, newDate)) {
                setDate(newDate);
            }
        }
        const { typeId , search , userLocation  } = queryParams;
        goTo('when', {
            typeId,
            storeId: store.globalStoreId,
            search,
            userLocation
        }) // TODO: MACC-19077 Check if this is correct.
        ;
    }, [
        pushClickedOnAlternativeStoreEvent,
        appointmentBookingUrlPaddedLength,
        isMultilingualEnabled,
        locale,
        appointmentBookingAlternativeUrl,
        appointmentBookingAlternativeUrlStores,
        setStore,
        setDate,
        queryParams,
        goTo,
        weeks1,
        date1,
        weekDate, 
    ]);
    React.useEffect(()=>{
        const { typeId , storeId  } = queryParams;
        /** Need to track when user go back/forth with browser navigation.
     * In this situation update event only with data based on query parameters.
     */ if (store1 && type && storeId === store1.globalStoreId && typeId === type.id) {
            const dataLayerEvent = bookAppointmentWhenStepScreenEvent({
                store: !isVirtualAppointment ? {
                    code: 0,
                    globalStoreId: store1.globalStoreId,
                    streetName: store1.streetName,
                    town: store1.town,
                    name: store1.name,
                    postalCode: store1.postalCode,
                    streetNumber: store1.streetNumber,
                    additionalStreetInfo: store1.additionalStreetInfo
                } : undefined,
                appointment: {
                    appointmentType: {
                        id: type.id,
                        ageGroup: type.ageGroup,
                        isVirtualAppointment
                    }
                }
            });
            DataLayer.pushEvent(dataLayerEvent);
        }
    }, [
        store1,
        type,
        queryParams,
        isVirtualAppointment
    ]);
    const aabWhenPageView = React.useCallback(()=>new Promise((resolve)=>{
            if (!store1 || !store1.globalStoreId || !type) {
                return;
            }
            resolve({
                id: 'VirtualPage-View',
                Page_Type: 'Info',
                Page_Section1: 'Exam',
                Page_Section2: 'Scheduler',
                Exam_Fitting: type.name,
                Store_Search_StoreId: store1.globalStoreId
            });
        })
    , [
        store1,
        type
    ]);
    useTrackPageView(aabWhenPageView);
    const noTimeSlotsAction = React.useCallback(()=>{
        switch(noTimeSlotsVariant){
            case Variant.time:
            case Variant.date:
            case Variant.closedDate:
                /* istanbul ignore if */ if (!nextAvailableDate) {
                    // This should never happen, but it's here to satisfy typescript
                    return;
                }
                setDate(nextAvailableDate);
                break;
            case Variant.week:
            case Variant.closedWeek:
                /* istanbul ignore if */ if (!nextWeekDate) {
                    // This should never happen, but it's here to satisfy typescript
                    return;
                }
                setWeekDate(nextWeekDate);
                setDate(nextWeekDate);
                break;
            case Variant.error:
                const globalStoreId = store1 === null || store1 === void 0 ? void 0 : store1.globalStoreId;
                var ref;
                const appointmentTypeId = (ref = type === null || type === void 0 ? void 0 : type.id) !== null && ref !== void 0 ? ref : undefined;
                /* istanbul ignore if */ if (!date1 || !globalStoreId || appointmentTypeId === undefined) {
                    // This should never happen, but it's here to satisfy typescript
                    return;
                }
                const dateString = DateUtils.toLocalDate(date1);
                getTimeSlots({
                    variables: {
                        globalStoreId,
                        appointmentTypeId,
                        date: dateString
                    }
                }, {
                    timeSlotsReferTo: {
                        date: dateString,
                        store: globalStoreId
                    }
                });
            case Variant.none:
                return;
        }
    }, [
        noTimeSlotsVariant,
        nextAvailableDate,
        nextWeekDate,
        getTimeSlots,
        store1,
        type,
        date1
    ]);
    const onToggleOtherTimeSlots = React.useCallback(()=>{
        setShowOtherTimeSlots((curr)=>!curr
        );
        setTimeout(()=>{
            triggerScroll();
        }, 50);
    }, [
        triggerScroll
    ]);
    React.useEffect(()=>{
        const dateIsAlreadySelected = Boolean(date1);
        const waitingForAvailability = !calendarAvailability.called || calendarAvailability.loading;
        if (dateIsAlreadySelected || !weeks1 || waitingForAvailability) {
            return;
        }
        const availableDate = firstAvailableDate(weeks1, calendarAvailability.data);
        const fallbackDate = today.current;
        setDate(availableDate !== null && availableDate !== void 0 ? availableDate : fallbackDate);
    }, [
        date1,
        weeks1,
        calendarAvailability
    ]);
    React.useEffect(()=>{
        if (!store1) {
            return;
        }
        getStoreOpeningTimes({
            variables: {
                globalStoreId: store1.globalStoreId
            }
        });
    }, [
        getStoreOpeningTimes,
        store1
    ]);
    React.useEffect(()=>{
        calendarAvailability.error && console.error('Error fetching appointment availability:', calendarAvailability.error.message);
    }, [
        calendarAvailability.error
    ]);
    React.useEffect(()=>{
        const globalStoreId = store1 === null || store1 === void 0 ? void 0 : store1.globalStoreId;
        var ref;
        const appointmentTypeId = (ref = type === null || type === void 0 ? void 0 : type.id) !== null && ref !== void 0 ? ref : undefined;
        /* istanbul ignore if */ if (!date1 || !globalStoreId || appointmentTypeId === undefined) {
            // This should never happen, but it's here to satisfy typescript
            return;
        }
        const dateString = DateUtils.toLocalDate(date1);
        getTimeSlots({
            variables: {
                globalStoreId,
                appointmentTypeId,
                date: dateString
            }
        }, {
            timeSlotsReferTo: {
                date: dateString,
                store: globalStoreId
            }
        });
    }, [
        getTimeSlots,
        store1,
        type,
        date1
    ]);
    React.useEffect(()=>setShowOtherTimeSlots(timeSlots.filtered.included.length === 0 && timeSlots.filtered.excluded.length > 0)
    , [
        timeSlots.filtered
    ]);
    const shouldRequestAlternativeStoreSuggestions = useShouldRequestAlternativeStoreSuggestions(timeSlots, date1, store1 !== null && store1 !== void 0 ? store1 : undefined, timeSlots.metadata);
    const hasNoTimeSlotsAvailableInCalendar = getCalendarTimeSlotsAvailability(calendarAvailability.data, noTimeSlotsVariant);
    return /*#__PURE__*/ React.createElement(AppointmentDateTime, swcHelpers.extends({}, rest, {
        otherTimeSlotsElement: otherTimeSlotsElement,
        labels: labels,
        onCalendarWeeksChange: onCalendarWeeksChange,
        store: store1,
        appointmentType: type,
        selectedDate: date1,
        onSelectDate: onSelectDate,
        calendarLoading: calendarLoading,
        calendarWeekDate: weekDate,
        calendarAvailability: calendarAvailability.data,
        calendarClosedDates: closedDates,
        calendarWeekCount: appointmentBookingAvailabiltyCalendarWeekCount,
        timeSlotsLoading: timeSlots.loading,
        timeSlots: timeSlots.filtered.included,
        onSelectTime: onSelectTimeSlot,
        noTimeSlotsVariant: noTimeSlotsVariant,
        noTimeSlotsAction: noTimeSlotsAction,
        nextAvailableDate: nextAvailableDate,
        noMoreAvailabilityUntilDate: nextWeekDate,
        shouldRequestAlternativeStoreSuggestions: shouldRequestAlternativeStoreSuggestions,
        showOtherTimeSlots: showOtherTimeSlots,
        onToggleOtherTimeSlots: onToggleOtherTimeSlots,
        otherTimeSlots: timeSlots.filtered.excluded,
        onSelectStore: onSelectStore,
        onPreferredTimeClick: onPreferredTimeClick,
        onPreferredTimeFilterClick: onPreferredTimeFilterClick,
        isAabAlternativeStoreAvailabilityEnabled: isAabAlternativeStoreAvailabilityEnabled,
        hasNoTimeSlotsAvailableInCalendar: hasNoTimeSlotsAvailableInCalendar,
        isVirtualappointment: isVirtualAppointment
    }));
};

export { AppointmentDateTimeContainer as default };
//# sourceMappingURL=AppointmentDateTimeContainer.js.map
